<template>
  <div>
    <ol class="breadcrumb">
      <li>
        <router-link to="/criar-contrato-manual">
          Geração manual de contratos
        </router-link>
      </li>
      <li class="active">
        Configurar contrato
      </li>
    </ol>
    <p class="admin-page-description">
      Configuração manual do contrato de um billing pendente. Defina
      parcelas, valores, descontos e dados do aluno passo a passo pra
      finalizar a configuração — usado quando a automação não conseguiu
      gerar o contrato sozinha.
    </p>
    <div v-if="billing">
      <div class="row">
        <div class="col-md-10">
          <div class="panel panel-default">
            <div class="panel-heading">
              <h4>
                {{ defineHeaderByStep }}
                <span
                  class="label label-white label-warning student-view__header-tag"
                  v-if="billing.origin === 'from_parcelamento'"
                >
                  Parcelamento
                </span>
              </h4>
            </div>
            <div class="panel-body">
              <div v-if="step === 'configureBilling'">
                <h4 class="dashboard-panel__title">
                  Dados do aluno
                </h4>
                <div class="row">
                  <div class="col-md-4">
                    <strong>Nome: </strong> {{ billing.student_name }}
                  </div>
                  <div class="col-md-4">
                    <strong>CPF: </strong> {{ billing.cpf }}
                  </div>
                  <div class="col-md-4">
                    <strong>Telefone: </strong> {{ billing.cellphone | formatCellphone }}
                  </div>
                </div>
                <hr class="divider divider--spaced-l-bottom">
                <h4 class="dashboard-panel__title">
                  Dados do curso
                </h4>
                <div class="row">
                  <div class="col-md-4">
                    <strong>Nome: </strong>{{ billing.course_name }}
                  </div>
                  <div class="col-md-4">
                    <strong>Sku: </strong>{{ billing.sku }}
                  </div>
                  <div class="col-md-4">
                    <strong>Instituição: </strong> {{ billing.institution_name }}
                  </div>
                </div>
                <hr class="divider divider--spaced-l-bottom">
                <h4 class="dashboard-panel__title">
                  Mensalidades
                </h4>
                <div class="row" style="margin-bottom: 24px">
                  <div class="col-md-4">
                    <strong>Total de mensalides do curso: </strong> {{ billing.course_installments }}
                  </div>
                  <div class="col-md-4">
                    <strong>Valor da mensalidade: </strong> {{ billing.installment_price | formatCurrency }}
                  </div>
                  <div class="col-md-4">
                    <strong>Valor total: </strong>
                    {{ (billing.installment_price * billing.course_installments) | formatCurrency }}
                  </div>
                </div>
                <hr class="divider divider--spaced-l-bottom">
                <h4 class="dashboard-panel__title dashboard-panel__title--no-margin-bottom">
                  Dados da cobrança
                </h4>
                <div class="dashboard-panel__sub-title">
                  <p>
                    Defina o mês e o ano de inicio da cobrança como se não houvesse isenção. As isenções serão definidas
                    na próxima tela.
                  </p>
                  <p>
                    <span class="label label-white label-primary">
                      Semestre de ingresso em {{ billing.enrollment_semester }}
                    </span>
                  </p>
                </div>
                <div class="form-section">
                  <div class="form-group form-section__input">
                    <label>Dia de vencimento</label>
                    <money v-bind="dueDayFormat"
                           v-model="billing.due_day"
                           class="form-control"
                    />
                    <span class="error-message js-due-day-error">{{ invalidFields.dueDay }}</span>
                  </div>
                  <div class="form-group form-section__input">
                    <label>Mês de inicio da cobrança</label>
                    <input type="number" v-model="startMonth" class="form-control" placeholder="Mês de inicio">
                    <span class="error-message js-start-month-error">{{ invalidFields.startMonth }}</span>
                  </div>
                  <div class="form-group form-section__input">
                    <label>Ano de inicio da cobrança</label>
                    <input v-model="startYear" type="number" class="form-control" placeholder="Ano de inicio">
                    <span class="error-message js-start-year-error">{{ invalidFields.startYear }}</span>
                  </div>
                </div>
                <hr class="divider divider--spaced-l-bottom">
                <h4 class="dashboard-panel__title">
                  Dados do contrato
                </h4>
                <div class="form-section">
                  <div class="form-group form-section__input">
                    <label>Quantidade de mensalidades do contrato:</label>
                    <p>
                      Quantidade de mensalidades que vai ser gerada para o contrato, não pode ser maior que a
                      quantidade total de mensalidades do curso.
                    </p>
                    <input v-model="contractInstallments" type="number" class="form-control"
                           placeholder="Numero de mensalidades do contrato"
                    >
                    <span class="error-message js-contract-installments-error">
                      {{ invalidFields.contractInstallments }}
                    </span>
                    <p>Total de mensalides do curso: {{ billing.course_installments }} mensalidades.</p>
                  </div>
                </div>
              </div>
              <div v-if="step === 'configureSpecialConditions'">
                <h4 class="dashboard-panel__title">
                  Benefícios
                </h4>
                <div class="well">
                  <ul v-if="billing.qb_exemptions_desc.length > 0">
                    <li v-for="(exemption, index) in billing.qb_exemptions_desc" :key="index">
                      {{ exemption }}
                    </li>
                  </ul>
                  <div v-else>
                    Não existem beneficios para essa cobrança
                  </div>
                </div>
                <div v-if="billing.origin === 'from_parcelamento'">
                  <h4 class="dashboard-panel__title">
                    Dados do parcelamento
                  </h4>
                  <div class="table-responsive">
                    <table class="table table-hover table-spaced">
                      <thead>
                        <tr>
                          <th>Durante o curso</th>
                          <th>Após o curso</th>
                          <th>Válido de</th>
                          <th>Válido até</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(rule, index) in billing.installment_rules" :key="index">
                          <td>{{ rule.during_course_percentage }}%</td>
                          <td>{{ rule.after_course_percentage }}%</td>
                          <td>{{ rule.valid_from | formatDate }}</td>
                          <td>{{ rule.valid_until | formatDate }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  Este é um billing de parcelamento, portanto os <strong>valores</strong> das mensalidades geradas
                  irão sofrer algumas alterações de acordo com as regras acima. Por exemplo:<br><br>
                  Se existe uma regra de 50% durante o curso e 50% depois do curso e o preço da mensalidade é de
                  R$ 100,00, será gerado uma mensalidade de R$ 50,00 e os outros R$ 50,00 o aluno terá que pagar depois
                  que finalizar o curso.<br><br>

                  <strong>
                    Gere as mensalidades como se essas regras não existissem, o valor correto será calculado
                    automáticamente. Então seguindo o exemplo acima, você deve colocar que o preço da mensalidade é
                    R$ 100,00. Depois quando você olhar na mensalidade o preço vai estar R$ 50,00.
                  </strong>
                </div>
                <hr class="divider divider--spaced-l-bottom">
                <h4 class="dashboard-panel__title">
                  Mensalidades
                </h4>
                <div class="table-responsive">
                  <table class="table table-hover table-spaced">
                    <thead>
                      <tr>
                        <th>Mensalidade</th>
                        <th>Vencimento</th>
                        <th>Valor</th>
                        <th>Desconto</th>
                        <th>Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="bill in bills"
                        :class="{ 'bill-disabled': bill.action === 'paidAtIes' }"
                        :key="bill.id"
                      >
                        <td>
                          <span class="contract__label--blue" v-if="bill.actualContract">
                            {{ bill.index }} ({{ months[bill.billMonth] }}/{{ bill.billYear }})
                          </span>
                          <span class="contract__label--gray" v-else>
                            {{ bill.index }} ({{ months[bill.billMonth] }}/{{ bill.billYear }})
                          </span>
                        </td>
                        <td>
                          <input class="form-control form-section__input--sm"
                                 v-mask="'##/##/####'"
                                 v-model="bill.dueDate"
                                 placeholder="dd/mm/yyyy"
                                 :disabled="bill.action === 'paidAtIes'"
                                 @change="notifyDueDateChange(bill)"
                                 :class="{ 'invalid-input': bill.errors.dueDate && bill.errors.dueDate !== ''}"
                          >
                          <span class="error-message">{{ bill.errors.dueDate }}</span>
                        </td>
                        <td>
                          <money v-bind="moneyFormat"
                                 v-model="bill.value"
                                 class="form-control form-section__input--sm" v-if="bill.action === 'abosulteExempt'"
                                 @change.native="notifyValueChange(bill)"
                                 :class="{ 'invalid-input': bill.errors.value && bill.errors.value !== ''}"
                          />
                          <span v-else>{{ bill.value | formatCurrency }}</span>
                          <span class="error-message">{{ bill.errors.value }}</span>
                        </td>
                        <td>
                          <money v-bind="percentageFormat"
                                 v-model="bill.discountPercentage"
                                 class="form-control form-section__input--xs"
                                 @change.native="notifyDiscountPercentageChange(bill)"
                                 v-if="bill.action === 'percentageExempt'"
                                 :class="{ 'invalid-input': bill.errors.discountPercentage &&
                                   bill.errors.discountPercentage !== ''}"
                          />
                          <span v-else>{{ bill.discountPercentage }} %</span>
                          <span class="error-message">{{ bill.errors.discountPercentage }}</span>
                        </td>
                        <td>
                          <select class="form-control" v-model="bill.action" @change="notifyActionChange(bill)">
                            <option value="">
                              Nenhum
                            </option>
                            <option value="paidAtIes">
                              Aluno pagou na faculdade
                            </option>
                            <option value="percentageExempt">
                              Isenção porcentagem
                            </option>
                            <option value="abosulteExempt">
                              Alteração de valor
                            </option>
                          </select>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="text-right">
                <button type="button" class="btn btn-primary" @click="showBilling()"
                        v-if="step === 'configureSpecialConditions'"
                >
                  <i class="fas fa-chevron-left"></i> Voltar
                </button>
                <button type="button" class="btn btn-primary" @click="submitForm()"
                        v-if="step === 'configureSpecialConditions'"
                >
                  Finalizar <i class="fas fa-check"></i>
                </button>
                <button type="button" class="btn btn-primary" @click="configureSpecialConditions()"
                        v-if="step === 'configureBilling'"
                >
                  Configurar mensalidades <i class="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      Esta cobrança não está pendente de configuração manual.
    </div>
  </div>
</template>
<script>
import moment from 'moment';
import AdminService from 'services/AdminService';

export default {
  data() {
    return {
      billing: null,
      billingId: this.$route.params.billingId,
      moneyFormat: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        precision: 2,
        masked: false,
      },
      installmentsFormat: {
        suffix: ' parcela(s)',
        precision: 0,
        masked: false,
      },
      dueDayFormat: {
        prefix: 'Todo dia ',
        thousands: '',
        precision: 0,
        masked: false,
      },
      percentageFormat: {
        suffix: ' %',
        precision: 0,
        masked: false,
      },
      startMonth: null,
      startYear: null,
      contractInstallments: '',
      invalidFields: {},
      step: 'configureBilling',
      bills: [],
      months: ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'],
      billsErrors: 0,
    };
  },
  computed: {
    defineHeaderByStep() {
      if (this.step === 'configureBilling') {
        return 'Configurar contrato';
      }
      return 'Configurar mensalidades';
    },
  },
  mounted() {
    this.loadBilling();
  },
  methods: {
    notifyDiscountPercentageChange(bill) {
      this.$set(bill.errors, 'discountPercentage', '');
      if (bill.discountPercentage < 0 || bill.discountPercentage > 100) {
        this.$set(bill.errors, 'discountPercentage', 'Valor inválido');
      } else {
        // eslint-disable-next-line no-param-reassign
        bill.value = this.billing.installment_price - (this.billing.installment_price
                                                       * (bill.discountPercentage / 100.0));
      }
      this.notifyDueDateChange(bill);
    },
    notifyValueChange(bill) {
      this.$set(bill.errors, 'value', '');
      if (bill.value <= 0) {
        this.$set(bill.errors, 'value', 'Valor inválido');
      }
      this.notifyDueDateChange(bill);
    },
    notifyDueDateChange(bill) {
      const date = moment(bill.dueDate, 'DD/MM/YYYY');
      this.$set(bill.errors, 'dueDate', '');
      if (bill.dueDate === '' || !date.isValid()) {
        this.$set(bill.errors, 'dueDate', 'Valor inválido');
      } else {
        const today = moment();
        const dueDate = moment(bill.dueDate, 'DD/MM/YYYY');
        if (bill.action !== 'paidAtIes' && bill.value > 0 && today >= dueDate) {
          this.$set(bill.errors, 'dueDate', 'Data retroativa inválida');
        }
      }
    },
    notifyActionChange(bill) {
      if (bill.action === '') {
        // eslint-disable-next-line no-param-reassign
        bill.value = this.billing.installment_price;
        // eslint-disable-next-line no-param-reassign
        bill.discountPercentage = 0;
        this.notifyDueDateChange(bill);
      } else if (bill.action === 'paidAtIes') {
        // eslint-disable-next-line no-param-reassign
        bill.value = this.billing.installment_price;
        // eslint-disable-next-line no-param-reassign
        bill.discountPercentage = 0;
        // eslint-disable-next-line no-param-reassign
        bill.dueDate = this.createDate(bill.index - 1);
        // eslint-disable-next-line no-param-reassign
        bill.errors = {};
      } else if (bill.action === 'percentageExempt') {
        // eslint-disable-next-line no-param-reassign
        bill.value = 0;
        // eslint-disable-next-line no-param-reassign
        bill.discountPercentage = 100;
        this.$set(bill.errors, 'value', '');
        this.notifyDueDateChange(bill);
      } else if (bill.action === 'abosulteExempt') {
        // eslint-disable-next-line no-param-reassign
        bill.value = this.billing.installment_price;
        // eslint-disable-next-line no-param-reassign
        bill.discountPercentage = 0;
        this.$set(bill.errors, 'discountPercentage', '');
        this.notifyDueDateChange(bill);
      }
    },
    loadBilling() {
      this.$loader.show();
      AdminService.getBillingPendingManualConfig(this.billingId)
        .then((response) => {
          this.billing = response.data;
          this.billing.due_day = parseInt(this.billing.due_day, 10);
          this.billing.installment_price = parseFloat(this.billing.installment_price);
          this.$loader.hide();
        })
        // eslint-disable-next-line no-unused-vars
        .catch((error) => {
          this.$loader.hide();
        });
    },
    configureSpecialConditions() {
      this.clearInvalidFields();
      this.validateFields();
      if (Object.keys(this.invalidFields).length === 0) {
        this.generateBills();
        this.showBills();
      }
    },
    clearInvalidFields() {
      this.invalidFields = {};
      const elementsClass = [
        '.js-contract-installments-error',
        '.js-start-year-error',
        '.js-start-month-error',
        '.js-due-day-error',
      ];
      for (let index = 0; index < elementsClass.length; index += 1) {
        const input = document.querySelector(elementsClass[index]).previousElementSibling;
        input.classList.remove('invalid-input');
      }
    },
    validateFields() {
      this.validateDueDay();
      this.validateStartYear();
      this.validateStartMonth();
      this.validateContractInstallments();
    },
    validateContractInstallments() {
      if (this.contractInstallments === '' || this.contractInstallments > this.billing.course_installments) {
        this.addErrorMessage('contractInstallments', '.js-contract-installments-error');
      }
    },
    validateStartYear() {
      if (!this.startYear || this.startYear <= 0) {
        this.addErrorMessage('startYear', '.js-start-year-error');
      }
    },
    validateStartMonth() {
      if (!this.startMonth || this.startMonth <= 0 || this.startMonth > 12) {
        this.addErrorMessage('startMonth', '.js-start-month-error');
      }
    },
    validateDueDay() {
      if (!this.billing.due_day || this.billing.due_day <= 0 || this.billing.due_day > 31) {
        this.addErrorMessage('dueDay', '.js-due-day-error');
      }
    },
    addErrorMessage(attribute, elementClass) {
      this.invalidFields[attribute] = 'Valor inválido';
      const input = document.querySelector(elementClass).previousElementSibling;
      input.classList.add('invalid-input');
    },
    showBills() {
      this.step = 'configureSpecialConditions';
    },
    showBilling() {
      this.step = 'configureBilling';
    },
    generateBills() {
      this.bills = [];
      let currentBill = {};
      for (let index = 1; index <= this.contractInstallments; index += 1) {
        currentBill.index = index;
        currentBill.actualContract = index <= this.contractInstallments;
        currentBill.dueDate = this.createDate(index - 1);
        currentBill.value = this.billing.installment_price;
        currentBill.discountPercentage = 0;
        currentBill.action = '';
        currentBill.errors = {};
        currentBill.billMonth = moment(currentBill.dueDate, 'DD/MM/YYYY').month();
        currentBill.billYear = moment(currentBill.dueDate, 'DD/MM/YYYY').year();
        this.applySpecialCondition(currentBill);
        this.notifyDueDateChange(currentBill);
        this.bills.push(currentBill);
        currentBill = {};
      }
    },
    createDate(position) {
      // Moment start January as month 0, so we need to decrement 1
      const billDueDate = moment([this.startYear, this.startMonth - 1]);
      billDueDate.add(position, 'month');
      const lastDayInMonth = billDueDate.daysInMonth();

      // If dueDay is larger than the last day in month, set the last day in month
      billDueDate.date(Math.min.apply(null, [this.billing.due_day, lastDayInMonth]));
      return billDueDate.format('DD/MM/YYYY');
    },
    applySpecialCondition(bill) {
      this.billing.exemptions.forEach((exemption) => {
        const positions = this.translateExemptionPositions(exemption.installments);
        positions.forEach((position) => {
          if (bill.index === position) {
            if (exemption.value_type === 'percentage') {
              // eslint-disable-next-line no-param-reassign
              bill.value -= bill.value * exemption.value;
              // eslint-disable-next-line no-param-reassign
              bill.action = 'percentageExempt';
              // eslint-disable-next-line no-param-reassign
              bill.discountPercentage = exemption.value * 100;
            } else if (exemption.value_type === 'absolute') {
              // eslint-disable-next-line no-param-reassign
              bill.value = exemption.value;
              // eslint-disable-next-line no-param-reassign
              bill.action = 'abosulteExempt';
            }
          }
        });
      });
    },
    translateExemptionPositions(installments) {
      const positions = [];
      installments.forEach((installment) => {
        if (installment < 0) {
          positions.push(this.billing.course_installments + installment + 1);
        } else {
          positions.push(installment);
        }
      });
      return positions;
    },
    submitForm() {
      if (this.validateBills()) {
        this.$loader.show();
        const billingData = this.buildBillingData();
        AdminService.createInitialContract({ billing: billingData })
          .then(() => {
            this.$alert.notifySuccess('Cobrança iniciada com sucesso');
            this.$router.push('/criar-contrato-manual');
            this.$loader.hide();
          })
          .catch((error) => {
            this.$alert.notifyAlert(`Erro ao iniciar a cobrança. ${error.message}`);
            this.$loader.hide();
          });
      }
    },
    validateBills() {
      let validBills = true;
      this.bills.forEach((bill) => {
        Object.keys(bill.errors).forEach((errorKey) => {
          if (bill.errors[errorKey] !== '') {
            validBills = false;
          }
        });
      });
      return validBills;
    },
    buildBillingData() {
      return {
        id: this.billing.id,
        start_month: this.startMonth,
        start_year: this.startYear,
        contract_installments: this.contractInstallments,
        bills: this.bills,
        due_day: this.billing.due_day,
      };
    },
    defineContractInstallments() {
      if (this.contractInstallments !== '') {
        this.contractInstallments = parseInt(this.contractInstallments, 10);
      }
    },
  },
};
</script>
